import axios from 'axios'
import Cookies from 'js-cookie'
import jwtDecode from 'jwt-decode'

import { Logger } from '../Logger'

const log = Logger('AuthUtils')

const AuthStorageKeys = {
  AuthToken: 'auth_token',
}

const initialize = () => {
  // Set the local storage token from the cookie on init
  const localStorageToken = localStorage.getItem(AuthStorageKeys.AuthToken)
  const cookieToken = Cookies.get(import.meta.env.VITE_COOKIE_NAME)
  if (localStorageToken === cookieToken) return

  // set directly to local storage
  if (cookieToken) {
    localStorage.setItem(AuthStorageKeys.AuthToken, cookieToken)
  }
}

class AuthUtils {
  constructor() {
    initialize()
    this.listeners = []
  }

  onTokenChange(listener) {
    function handler({ key, newValue }) {
      if (key === AuthStorageKeys.AuthToken) {
        listener(newValue)
      }
    }

    this.listeners.push(listener)

    window.addEventListener('storage', handler)

    return {
      unsubscribe: () => {
        this.listeners = this.listeners.filter((l) => l !== listener)
        window.removeEventListener('storage', handler)
      },
    }
  }

  _notifyTokenChange() {
    const authToken = this.getAuthToken()
    this.listeners.forEach((listener) => listener(authToken))
  }

  getAuthToken() {
    return localStorage.getItem(AuthStorageKeys.AuthToken)
  }

  setAuthToken(token) {
    localStorage.setItem(AuthStorageKeys.AuthToken, token)
    this._notifyTokenChange()
  }

  clearAuthToken() {
    localStorage.removeItem(AuthStorageKeys.AuthToken)
    this._notifyTokenChange()
  }

  isAuthenticated() {
    return Boolean(this.getUserId())
  }

  /**
   * Logout the currently logged in user on this device.
   *
   * This will handle all the required actions to correctly logout the user.
   * It doesn't use existing axios client to avoid recursion and be independent of interceptors
   *
   * @returns {Promise<void>}
   */
  logout() {
    window.branch?.logout()
    window.queryClient?.clear()
    window.honeybadger?.clear()
    window.honeybadger?.resetMaxErrors()
    this.unsubscribeWebPush()

    const token = this.getAuthToken()
    if (!token) return Promise.resolve()

    this.clearAuthToken()

    return axios
      .post('auth/logout', undefined, {
        baseURL: import.meta.env.VITE_API_URL,
        withCredentials: true,
        headers: { Authorization: token },
      })
      .catch((error) => log.error('Logout error', error))
  }

  unsubscribeWebPush() {
    if (!navigator.serviceWorker) return Promise.resolve()

    return navigator.serviceWorker
      .getRegistration()
      .then((registration) => registration?.pushManager?.getSubscription())
      .then((subscription) => subscription?.unsubscribe())
      .catch(() => {})
  }

  getUserId(token = this.getAuthToken()) {
    const decoded = this.decodeToken(token)
    return decoded?.user_id
  }

  decodeToken(token) {
    if (!token) return null

    try {
      return jwtDecode(token)
    } catch {
      return null
    }
  }
}

const authUtils = new AuthUtils()

export default authUtils
